import { PipelineTab } from '../../../shared/model/pipeline-tab';
import { MedicalCase, MedicalCaseProcessingPhase } from '../../../domain/medical-case/model';
import { PreparationStepKey, ProcessingPhasePairKey } from './pipeline-step.model';
import type { FlowPipelineDefinition } from './pipeline-step.model';

export const PREPARATION_TABS: readonly Omit<PipelineTab, 'disabled'>[] = [
  { labelKey: 'DOMAIN.CASE.PIPELINE.TAB.UPLOAD', route: 'upload' },
  { labelKey: 'DOMAIN.CASE.PIPELINE.TAB.PREVIEW', route: 'preview' },
  { labelKey: 'DOMAIN.CASE.PIPELINE.TAB.DIGITIZATION', route: 'digitization' },
  { labelKey: 'DOMAIN.CASE.PIPELINE.TAB.TRANSLATE', route: 'translate' },
  { labelKey: 'DOMAIN.CASE.PIPELINE.TAB.ANONYMIZE', route: 'anonymize' },
  { labelKey: 'DOMAIN.CASE.PIPELINE.TAB.SUMMARY', route: 'summary' },
  { labelKey: 'DOMAIN.CASE.PIPELINE.TAB.PACKAGE', route: 'package' },
] as const;

export const PREPARATION_STEP_ORDER: readonly PreparationStepKey[] = [
  'upload',
  'preview',
  'digitization',
  'translate',
  'anonymize',
  'summary',
  'package',
] as const;

export const PREPARATION_PROCESSING_PAIR_TO_STEP_MAP: Readonly<
  Partial<Record<ProcessingPhasePairKey, PreparationStepKey>>
> = {
  'MEDICAL_RECORD_INIT->MEDICAL_RECORD_DIGITIZATION': 'digitization',
  'MEDICAL_RECORD_DIGITIZATION->MEDICAL_RECORD_DIGITIZATION': 'digitization',
  'MEDICAL_RECORD_TRANSLATION->MEDICAL_RECORD_TRANSLATION': 'translate',
  'MEDICAL_RECORD_ANONYMIZATION->MEDICAL_RECORD_ANONYMIZATION': 'anonymize',
  'MEDICAL_RECORD_INIT->MEDICAL_RECORD_ANONYMIZATION': 'anonymize',
  'MEDICAL_RECORD_SUMMARY->MEDICAL_RECORD_SUMMARY': 'summary',
  'MEDICAL_RECORD_DIGITIZATION->MEDICAL_RECORD_TRANSLATION': 'translate',
  'MEDICAL_RECORD_TRANSLATION->MEDICAL_RECORD_ANONYMIZATION': 'anonymize',
  'MEDICAL_RECORD_ANONYMIZATION->MEDICAL_RECORD_SUMMARY': 'summary',
  'MEDICAL_RECORD_SUMMARY->MEDICAL_RECORD_PACKAGING': 'package',
  'MEDICAL_RECORD_DOSSIER->MEDICAL_RECORD_PACKAGING': 'package',
  'MEDICAL_RECORD_PACKAGING->MEDICAL_RECORD_PACKAGING': 'package',
};

export const PREPARATION_PHASE_TO_STEP_MAP: Readonly<
  Partial<Record<MedicalCaseProcessingPhase, PreparationStepKey>>
> = {
  MEDICAL_RECORD_INIT: 'preview',
  MEDICAL_RECORD_DIGITIZATION: 'digitization',
  MEDICAL_RECORD_TRANSLATION: 'translate',
  MEDICAL_RECORD_ANONYMIZATION: 'anonymize',
  MEDICAL_RECORD_SUMMARY: 'summary',
  MEDICAL_RECORD_PACKAGING: 'package',
};

export const PREPARATION_STEP_TO_PHASE_MAP: Readonly<
  Partial<Record<PreparationStepKey, MedicalCaseProcessingPhase>>
> = {
  preview: 'MEDICAL_RECORD_INIT',
  digitization: 'MEDICAL_RECORD_DIGITIZATION',
  translate: 'MEDICAL_RECORD_TRANSLATION',
  anonymize: 'MEDICAL_RECORD_ANONYMIZATION',
  summary: 'MEDICAL_RECORD_SUMMARY',
  package: 'MEDICAL_RECORD_PACKAGING',
};

export const PREPARATION_PIPELINE_DEFINITION: FlowPipelineDefinition = {
  tabs: PREPARATION_TABS,
  stepOrder: PREPARATION_STEP_ORDER,
  defaultUnlockedSteps: ['upload', 'preview'],
  processingPairToStepMap: PREPARATION_PROCESSING_PAIR_TO_STEP_MAP,
  phaseToStepMap: PREPARATION_PHASE_TO_STEP_MAP,
  stepToPhaseMap: PREPARATION_STEP_TO_PHASE_MAP,
  completionPhases: [
    { step: 'preview', phase: 'MEDICAL_RECORD_INIT' },
    { step: 'digitization', phase: 'MEDICAL_RECORD_DIGITIZATION' },
    { step: 'translate', phase: 'MEDICAL_RECORD_TRANSLATION' },
    { step: 'anonymize', phase: 'MEDICAL_RECORD_ANONYMIZATION' },
    { step: 'summary', phase: 'MEDICAL_RECORD_SUMMARY' },
    { step: 'package', phase: 'MEDICAL_RECORD_PACKAGING' },
  ],
  fileIdResolvers: {
    anonymize: (mc: MedicalCase) => mc.anonymizedFileId != null,
    summary: (mc: MedicalCase) => mc.summaryFileId != null,
  },
  outdatedRules: {
    summary: { dependencyPhases: ['MEDICAL_RECORD_ANONYMIZATION', 'MEDICAL_RECORD_ANONYMIZED_SUMMARY'], nullGuardPhase: 'MEDICAL_RECORD_SUMMARY' },
  },
};
